/**
 * County abstraction for the Florida county-expansion effort.
 *
 * The backend now returns a closed `countyCode` (on property/profile DTOs) and,
 * per-case, a computed `filingDeadline`/`filingDeadlineIsEstimated` and
 * `complianceCopy` (see `CaseResponse` in `@kin/property-tax-types`). Components
 * should read those real values first. What's left here is:
 * - `County` / `COUNTY_COPY`: presentational copy the backend doesn't own (page
 *   titles, the "protest"/"petition" noun, sign-page document-name fallback) —
 *   these are frontend display choices, not backend facts.
 * - `getFilingDeadline`/`daysUntilDeadline`: a fallback used only when no real
 *   backend value is available yet — before a case exists (nothing to fetch
 *   `filingDeadline` from), or when `filingDeadlineIsEstimated` is true and the
 *   real per-year FL TRIM-notice date hasn't been sourced. Never presented as
 *   more authoritative than the real value once one exists.
 */

/** Closed set of counties this app currently supports. */
export type County = 'harris' | 'manatee' | 'osceola';

const JURISDICTION_COUNTY: Record<string, County> = {
  HARRIS_TX: 'harris',
  MANATEE_FL: 'manatee',
  OSCEOLA_FL: 'osceola',
};

/** The county for a jurisdiction code (e.g. `HARRIS_TX`), or null for one this app has no copy for. */
export function countyForJurisdiction(jurisdictionCode: string | null | undefined): County | null {
  return (jurisdictionCode && JURISDICTION_COUNTY[jurisdictionCode]) || null;
}

/** Static, per-county copy. Filing-deadline copy is intentionally NOT included here. */
export interface CountyCopy {
  documentName: string;
  guaranteeCopy: string;
  regulatoryFooter: string;
  /** Lowercase noun for inline sentences, e.g. "file your {filingNoun}" — 'protest' | 'petition'. */
  filingNoun: string;
  /** Title-case copy for page <h1> titles, e.g. 'Property Tax Protest' | 'Property Tax Petition'. */
  pageTitle: string;
  /** Office whose public record the sign-up page shows; set only for counties sign-up supports. */
  appraisalDistrict?: string;
}

export const COUNTY_COPY: Record<County, CountyCopy> = {
  harris: {
    documentName: 'Texas Form 50-162',
    guaranteeCopy: "There's no risk. In Texas, a protest can only lower your assessed value — never raise it.",
    regulatoryFooter:
      'In Texas, all Property Tax Consulting services are regulated by The Texas Department of Licensing and ' +
      'Regulation, P.O. Box 12157, Austin, Texas 78711, 1-800-803-9202, 512-463-6599; ' +
      'https://www.tdlr.texas.gov/. Kin Insurance, Inc.',
    filingNoun: 'protest',
    pageTitle: 'Property Tax Protest',
    appraisalDistrict: 'Harris County Appraisal District',
  },
  // PLACEHOLDER — not legal/ops-reviewed copy. Manatee County Authorization is a stand-in
  // for a real HelloSign template that doesn't exist yet, and the copy below intentionally
  // omits any raise/lower assessed-value claim since Florida's VAB process hasn't been
  // confirmed to work that way.
  manatee: {
    documentName: 'Manatee County Authorization',
    guaranteeCopy: "Your fee is 10% of whatever you save — and only if we win. If we don't win, you owe nothing.",
    regulatoryFooter:
      'PLACEHOLDER — Florida regulatory footer not yet confirmed for Manatee County.',
    filingNoun: 'petition',
    pageTitle: 'Property Tax Petition',
  },
  // PLACEHOLDER — not legal/ops-reviewed copy. Osceola County Authorization is a stand-in
  // for a real HelloSign template that doesn't exist yet, and the copy below intentionally
  // omits any raise/lower assessed-value claim since Florida's VAB process hasn't been
  // confirmed to work that way.
  osceola: {
    documentName: 'Osceola County Authorization',
    guaranteeCopy: "Your fee is 10% of whatever you save — and only if we win. If we don't win, you owe nothing.",
    regulatoryFooter:
      'PLACEHOLDER — Florida regulatory footer not yet confirmed for Osceola County.',
    filingNoun: 'petition',
    pageTitle: 'Property Tax Petition',
  },
};

/**
 * Shared `DatePipe` format for filing-deadline dates. Both `confirm.component.html` and
 * `property-sidebar.component.html` bind to this rather than each picking their own format
 * literal, so the two views can't silently drift apart again.
 */
export const FILING_DEADLINE_DATE_FORMAT = 'MMMM d, y';

/** Fixed Harris County (Texas) ARB filing deadline, replicated verbatim from the former src/app/deadline.ts. */
const HARRIS_DEADLINE = new Date(2026, 4, 15); // May 15, 2026

/**
 * Computes the filing deadline for a given county.
 *
 * - `'harris'`: reproduces the exact existing behavior — a fixed May 15, 2026 date,
 *   regardless of `referenceDate` — to avoid any behavior change for existing users.
 * - `'manatee'` / `'osceola'`: PLACEHOLDER, not legally accurate. Computed as
 *   `referenceDate + 25 days` as a stand-in so the UI has *a* date to show. Florida's
 *   real VAB petition deadline is 25 days after the county mails its TRIM notice — a
 *   per-county/year date the frontend doesn't have data for yet. This must be replaced
 *   once that data is available.
 */
export function getFilingDeadline(county: County, referenceDate: Date = new Date()): Date {
  if (county === 'harris') {
    return HARRIS_DEADLINE;
  }
  const deadline = new Date(referenceDate);
  deadline.setDate(deadline.getDate() + 25);
  return deadline;
}

/**
 * Days remaining (never negative) until an already-known deadline. Shared by
 * `daysUntilDeadline` below and by callers that have a real, backend-provided
 * deadline (rather than one computed from `getFilingDeadline`) to diff against.
 */
export function daysUntil(deadline: Date, referenceDate: Date = new Date()): number {
  const diff = deadline.getTime() - referenceDate.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/** Days remaining (never negative) until the given county's fallback filing deadline. */
export function daysUntilDeadline(county: County, referenceDate: Date = new Date()): number {
  return daysUntil(getFilingDeadline(county, referenceDate), referenceDate);
}

/**
 * Parses a date-only ISO string (`YYYY-MM-DD`, e.g. the backend's `filingDeadline`)
 * into a local-time `Date` at midnight on that calendar day. Deliberately not
 * `new Date(iso)` — that parses as UTC midnight, which renders as the *previous*
 * day in any timezone behind UTC.
 */
export function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}
